import { RotateRightRounded as RotateRightIcon } from "@mui/icons-material";
import { Button, Chip, Menu, MenuItem, Typography, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as dates from "common/dates";
import { abbreviated } from "common/numbers";
import DefaultLoader from "components/common/DefaultLoader";
import Disabled from "components/common/Disabled";
import IconButton from "components/common/IconButton";
import StatusBadge from "components/common/StatusBadgeV2";
import * as labels from "config.labels";
import { useCallback, useState, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as staticDataService from "services/staticDataService";
import * as submissionsService from "services/submissionsService";
import * as persistenceActions from "store/actions/persistence/persistenceActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";

const RenewalSubmissionMenuItem = ({
  submission,
  currentSubmissionId,
  onCardClick,
  isLatest,
  onRenewClick,
}) => {
  const theme = useTheme();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  return (
    <MenuItem
      key={submission.id}
      sx={{
        padding: "12px 16px",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        cursor: "pointer",
        backgroundColor:
          submission.submissionId === currentSubmissionId
            ? theme.brand.accent[100]
            : "transparent",
        "&:hover": {
          backgroundColor:
            submission.submissionId === currentSubmissionId
              ? `${theme.brand.accent[300]}cc`
              : `${theme.brand.black}14`,
        },
      }}
      onClick={() => onCardClick(submission.submissionId)}
    >
      <Stack
        direction={"column"}
        sx={{
          position: "relative",
          width: "100%",
          paddingRight: isLatest ? "80px" : "0",
        }}
      >
        <Typography
          variant={"subtitle1"}
          style={{ fontWeight: "bold", marginBottom: "4px" }}
        >
          {
            dates.strDateToYearMonthDay(
              submission.effectiveDate ?? submission.inception
            )?.year
          }
        </Typography>
        <Stack
          direction={"row"}
          sx={{
            flexWrap: "wrap",
            gap: "8px",
            alignItems: "center",
          }}
        >
          {submission.insured && (
            <Typography variant={"body2"} style={{ fontWeight: "500" }}>
              {submission.insured}
            </Typography>
          )}
          {submission.industryName && (
            <Typography variant={"caption"} color={"textSecondary"}>
              {submission.industryName}
            </Typography>
          )}
          {submission.reference && (
            <Typography variant={"caption"} color={"textSecondary"}>
              {"Ref: "}
              {submission.reference}
            </Typography>
          )}
        </Stack>
        {submission.layers && submission.layers.length > 0 && (
          <Stack
            direction={"row"}
            sx={{
              flexWrap: "wrap",
              gap: "6px",
              marginTop: "8px",
            }}
          >
            {submission.layers
              .filter(
                (layer) => layer.limit != null && layer.attachment != null
              )
              .map((layer) => (
                <Chip
                  key={layer.id}
                  avatar={
                    <StatusBadge status={layer.status} variant={"icon"} />
                  }
                  label={`${abbreviated(layer.limit)} xs ${abbreviated(
                    layer.attachment
                  )}`}
                  size={"small"}
                  variant={"outlined"}
                  style={{ fontSize: "11px" }}
                />
              ))}
          </Stack>
        )}
        <Stack
          direction={"row"}
          sx={{
            position: "absolute",
            top: "0",
            right: "0",
            gap: "4px",
          }}
        >
          {isLatest && (
            <Disabled ifReadOnly>
              <Button
                variant={"contained"}
                size={"small"}
                disableElevation
                onClick={(e) => {
                  e.stopPropagation();
                  onRenewClick();
                }}
              >
                {translateLabel(labels.RENEW_MENU_RENEW_BUTTON, {
                  default: "+ Renew",
                })}
              </Button>
            </Disabled>
          )}
        </Stack>
      </Stack>
    </MenuItem>
  );
};

const RenewalMenuComponent = ({
  isOpen,
  anchorEl,
  onClose,
  currentSubmissionId,
  renewSubmission,
  onNavigateToSubmission,
}) => {
  const navigate = useNavigate();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const {
    data: renewalSubmissions,
    refetch: refetchRenewalSubmissions,
    isFetching: renewalSubmissionsFetching,
    isError: renewalSubmissionsError,
  } = submissionsService.useListRenewalSubmissionsQuery(
    { ids: currentSubmissionId ? [currentSubmissionId] : [] },
    { skip: !currentSubmissionId }
  );

  const processedSubmissions = renewalSubmissions?.submissions
    ? Object.values(renewalSubmissions.submissions).sort((a, b) => {
        const dateA = a.effectiveDate ?? a.inception;
        const dateB = b.effectiveDate ?? b.inception;
        return dateB.localeCompare(dateA); // newest first
      })
    : null;

  const handleRenewClick = () => {
    onClose();
    createRenewalSubmission();
  };

  const handleCardClick = (submissionId) => {
    onNavigateToSubmission(submissionId);
  };

  useEffect(() => {
    if (
      processedSubmissions == null &&
      !renewalSubmissionsFetching &&
      !renewalSubmissionsError
    ) {
      refetchRenewalSubmissions();
    }
  }, [
    processedSubmissions,
    refetchRenewalSubmissions,
    renewalSubmissionsFetching,
    renewalSubmissionsError,
  ]);

  const createRenewalSubmission = useCallback(() => {
    if (processedSubmissions?.length > 0) {
      if (currentSubmissionId === processedSubmissions[0].submissionId) {
        renewSubmission({
          navigate,
        });
      } else {
        renewSubmission({
          navigate,
          submissionId: processedSubmissions[0].submissionId,
        });
      }
    }
  }, [renewSubmission, currentSubmissionId, processedSubmissions, navigate]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      MenuListProps={{
        "aria-labelledby": "renew-button",
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      disableEnforceFocus
      disableRestoreFocus
      disablePortal={false}
    >
      {renewalSubmissionsError ? (
        <Stack gap={1} sx={{ padding: "8px 16px" }}>
          <Typography variant={"subtitle2"} color={"grey"}>
            {translateLabel(labels.RENEW_MENU_FETCH_ERROR, {
              default: "Sorry, there was an error fetching renewal details",
            })}
          </Typography>
          <Button
            size={"small"}
            variant={"contained"}
            disableElevation
            onClick={() => refetchRenewalSubmissions()}
          >
            {translateLabel(labels.RENEW_MENU_RETRY_BUTTON, {
              default: "Try Again",
            })}
          </Button>
        </Stack>
      ) : renewalSubmissionsFetching || processedSubmissions == null ? (
        <MenuItem
          sx={{
            padding: "12px 16px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <DefaultLoader size={24} />
        </MenuItem>
      ) : (
        processedSubmissions?.map((submission, index) => (
          <RenewalSubmissionMenuItem
            key={submission.submissionId}
            submission={submission}
            currentSubmissionId={currentSubmissionId}
            onCardClick={handleCardClick}
            isLatest={index === 0}
            onRenewClick={handleRenewClick}
          />
        ))
      )}
    </Menu>
  );
};

const RenewalMenu = ({ submissionId, renewSubmission, isNew }) => {
  const navigate = useNavigate();
  const [renewAnchorEl, setRenewAnchorEl] = useState(null);
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const isRenewMenuOpen = Boolean(renewAnchorEl);

  const useRenewalMenu = staticDataService.useFeatureFlag(
    "use-improved-renewal-ui",
    {
      default: true,
    }
  );

  const createRenewalSubmission = useCallback(() => {
    renewSubmission({ navigate });
  }, [navigate, renewSubmission]);

  const handleRenewClick = (event) => {
    if (event?.shiftKey || !useRenewalMenu) {
      createRenewalSubmission();
    } else {
      setRenewAnchorEl(event.currentTarget);
    }
  };

  const handleRenewMenuClose = () => {
    setRenewAnchorEl(null);
  };

  const handleNavigateToSubmission = (targetSubmissionId) => {
    navigate(`/submissions/${targetSubmissionId}`);
  };

  if (isNew) {
    return null;
  }

  return (
    <Disabled ifReadOnly>
      <>
        <div>
          <IconButton
            id={"renewal-button"}
            icon={RotateRightIcon}
            onClick={handleRenewClick}
            className={"link"}
            tooltip={translateLabel(labels.RENEW_BUTTON_TOOLTIP, {
              default: "Renew",
            })}
            aria-controls={"renew-menu"}
            aria-haspopup={"true"}
            aria-expanded={isRenewMenuOpen ? "true" : "false"}
          />
        </div>
        <RenewalMenuComponent
          isOpen={isRenewMenuOpen}
          anchorEl={renewAnchorEl}
          onClose={handleRenewMenuClose}
          currentSubmissionId={submissionId}
          renewSubmission={renewSubmission}
          onNavigateToSubmission={handleNavigateToSubmission}
        />
      </>
    </Disabled>
  );
};

const mapDispatchToProps = {
  renewSubmission: persistenceActions.renewSubmission,
};

const mapStateToProps = (state) => {
  return {
    isNew: persistenceSelectors.selectIsNew(state),
    submissionId: persistenceSelectors.selectSubmissionId(state),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(RenewalMenu);
